import { api } from './api';

// =====================================================
// Tipos locales (espejo liviano de @suminuta/shared)
// =====================================================

export type TrainingRol =
  | 'administrador'
  | 'operador'
  | 'supervisor'
  | 'encargado_inventario';
export type AttemptStatus = 'in_progress' | 'passed' | 'failed';
export type ModuleProgressStatus = 'pendiente' | 'en_progreso' | 'aprobado' | 'reprobado';

export interface TrainingOpcion {
  id: 'a' | 'b' | 'c' | 'd';
  texto: string;
}

export interface TrainingModule {
  id: number;
  slug: string;
  nombre: string;
  descripcion: string;
  orden: number;
  video_url: string | null;
  manual_url: string | null;
  manual_nombre: string | null;
  umbral_aprobacion: number;
  tiempo_reintento_horas: number;
  roles_aplicables: TrainingRol[];
  activo: boolean;
}

export interface TrainingQuestionPublic {
  id: number;
  modulo_id: number;
  pregunta: string;
  opciones: TrainingOpcion[];
}

export interface TrainingUser {
  id: number;
  nombre: string;
  email: string;
  empresa: string;
  rol: TrainingRol;
  telefono: string | null;
}

export interface RespuestaEvaluada {
  preguntaId: number;
  respuesta: 'a' | 'b' | 'c' | 'd';
  correcta: boolean;
  respuesta_correcta?: 'a' | 'b' | 'c' | 'd';
  explicacion?: string | null;
}

export interface ModuleProgress {
  modulo: TrainingModule;
  status: ModuleProgressStatus;
  mejor_puntaje: number | null;
  ultimo_intento_at: string | null;
  ultimo_intento_id: number | null;
  reintentar_despues_de: string | null;
  aplicable_al_rol: boolean;
}

export interface StartResponse {
  attemptId: number;
  modulo: { id: number; slug: string; nombre: string; umbral_aprobacion: number };
  preguntas: TrainingQuestionPublic[];
}

export interface SubmitResponse {
  attemptId: number;
  puntaje: number;
  aprobado: boolean;
  umbral: number;
  respuestas: RespuestaEvaluada[];
}

// =====================================================
// Cliente API
// =====================================================

export const trainingApi = {
  // Público
  getModules: async (): Promise<TrainingModule[]> => {
    const { data } = await api.get<{ success: boolean; data: TrainingModule[] }>(
      '/training/modules'
    );
    return data.data;
  },

  registrarUsuario: async (payload: {
    nombre: string; email: string; empresa: string;
    rol: TrainingRol; telefono?: string;
  }): Promise<TrainingUser & { reused?: boolean }> => {
    const { data } = await api.post<{ success: boolean; data: TrainingUser & { reused?: boolean } }>(
      '/training/users',
      payload
    );
    return data.data;
  },

  getProgress: async (usuarioId: number): Promise<{
    usuario: TrainingUser;
    progreso: ModuleProgress[];
  }> => {
    const { data } = await api.get<{ success: boolean; data: { usuario: TrainingUser; progreso: ModuleProgress[] } }>(
      `/training/users/${usuarioId}/progress`
    );
    return data.data;
  },

  startAttempt: async (payload: { usuarioId: number; moduloId: number }): Promise<StartResponse> => {
    const { data } = await api.post<{ success: boolean; data: StartResponse }>(
      '/training/attempts/start',
      payload
    );
    return data.data;
  },

  submitAttempt: async (attemptId: number, respuestas: { preguntaId: number; respuesta: 'a' | 'b' | 'c' | 'd' }[]): Promise<SubmitResponse> => {
    const { data } = await api.post<{ success: boolean; data: SubmitResponse }>(
      `/training/attempts/${attemptId}/submit`,
      { respuestas }
    );
    return data.data;
  },

  getAttempt: async (attemptId: number): Promise<any> => {
    const { data } = await api.get<{ success: boolean; data: any }>(
      `/training/attempts/${attemptId}`
    );
    return data.data;
  },

  // Admin
  admin: {
    getStats: async () => {
      const { data } = await api.get<{ success: boolean; data: any }>('/training/admin/stats');
      return data.data;
    },
    getAttempts: async (filtros: Record<string, any> = {}) => {
      const { data } = await api.get<{ success: boolean; data: any[]; pagination: any }>(
        '/training/admin/attempts',
        { params: filtros }
      );
      return { data: data.data, pagination: data.pagination };
    },
    getUsuarios: async () => {
      const { data } = await api.get<{ success: boolean; data: any[] }>('/training/admin/users');
      return data.data;
    },
    downloadExcel: async (filtros: Record<string, any> = {}): Promise<Blob> => {
      const response = await api.get('/training/admin/export', {
        params: filtros,
        responseType: 'blob',
      });
      return response.data as Blob;
    },
    getModules: async () => {
      const { data } = await api.get<{ success: boolean; data: TrainingModule[] }>(
        '/training/admin/modules'
      );
      return data.data;
    },
    updateModule: async (id: number, payload: Partial<TrainingModule>) => {
      const { data } = await api.put(`/training/admin/modules/${id}`, payload);
      return (data as any).data;
    },
    getQuestions: async (moduloId: number) => {
      const { data } = await api.get<{ success: boolean; data: any[] }>(
        '/training/admin/questions',
        { params: { moduloId } }
      );
      return data.data;
    },
    createQuestion: async (payload: any) => {
      const { data } = await api.post('/training/admin/questions', payload);
      return (data as any).data;
    },
    updateQuestion: async (id: number, payload: any) => {
      const { data } = await api.put(`/training/admin/questions/${id}`, payload);
      return (data as any).data;
    },
    deleteQuestion: async (id: number) => {
      const { data } = await api.delete(`/training/admin/questions/${id}`);
      return data;
    },
  },
};
